import Difficulty from 'app/frontend/types/custom/Difficulty'
import RaidPlan, { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import { deleteRequest, getRequest, postRequest, putRequest } from '.'

type RaidPlanOwner =
  | { ownerType: 'team'; ownerId: number }
  | { ownerType: 'user' }

type UserRaidPlansIndex = {
  raidPlans: RaidPlan[]
  teamPlanCounts: Record<number, number>
}

const ownerPath = (owner: RaidPlanOwner) => {
  if (owner.ownerType === 'team')
    return `/api/teams/${owner.ownerId}/raid_plans`
  return '/api/user/raid_plans'
}

const paramsFor = (seasonId?: number, difficulty?: Difficulty) => {
  return new URLSearchParams(
    Object.entries({
      ...(seasonId ? { season_id: seasonId.toString() } : {}),
      ...(difficulty ? { difficulty } : {}),
    }),
  ).toString()
}

const raidPlansUrl = (
  owner: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
) => {
  const params = paramsFor(seasonId, difficulty)
  return `${ownerPath(owner)}${params ? `?${params}` : ''}`
}

export const getRaidPlansIndex = async (
  owner: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
): Promise<UserRaidPlansIndex | RaidPlan[]> => {
  return await getRequest(raidPlansUrl(owner, seasonId, difficulty))
}

export const getRaidPlans = async (
  owner: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
): Promise<RaidPlan[]> => {
  const response = await getRaidPlansIndex(owner, seasonId, difficulty)
  return Array.isArray(response) ? response : response.raidPlans
}

export const getRaidPlan = async (
  owner: RaidPlanOwner,
  planId: number,
): Promise<RaidPlan> => {
  return await getRequest(`${ownerPath(owner)}/${planId}`)
}

export const createRaidPlan = async (
  owner: RaidPlanOwner,
  raidPlan: {
    encounter_id: number
    difficulty: Difficulty
    name: string
    timeline?: RaidPlanTimeline
  },
): Promise<RaidPlan> => {
  return await postRequest(ownerPath(owner), {
    raid_plan: raidPlan,
  })
}

export const updateRaidPlan = async (
  owner: RaidPlanOwner,
  planId: number,
  raidPlan: {
    encounter_id?: number
    difficulty?: Difficulty
    name?: string
    message?: string
    timeline?: RaidPlanTimeline
  },
): Promise<RaidPlan> => {
  return await putRequest(`${ownerPath(owner)}/${planId}`, {
    raid_plan: raidPlan,
  })
}

export const deleteRaidPlan = async (
  owner: RaidPlanOwner,
  planId: number,
): Promise<{ success: boolean }> => {
  return await deleteRequest(`${ownerPath(owner)}/${planId}`)
}

export const getRaidPlanTopExecutionKills = async (
  owner: RaidPlanOwner,
  encounterId: number,
): Promise<wowaudit.RaidReportTopExecutionKill[]> => {
  return await getRequest(
    `${ownerPath(owner)}/top_execution_kills?encounter_id=${encounterId}`,
  )
}

export const importRaidPlanTopExecutionKillTimeline = async (
  owner: RaidPlanOwner,
  planId: number,
  topExecutionKillId: number,
): Promise<RaidPlan> => {
  return await postRequest(
    `${ownerPath(owner)}/${planId}/import_top_execution_kill_timeline`,
    { topExecutionKillId },
  )
}

export type { RaidPlanOwner, UserRaidPlansIndex }
